import type { Validators } from '@quickbit/qb-design-system';
import { KybFormData } from '../types';

/**
 * Validates a Legal Entity Identifier (LEI) based on ISO 17442.
 * Completely generated by ChatGPT so it takes the blame if it's wrong.
 *
 * @param lei The LEI string to validate.
 * @returns True if the LEI is valid, false otherwise.
 */
const isValidLEI = (lei: string) => {
  // LEI must be exactly 20 characters long
  if (lei.length !== 20) {
    return false;
  }

  // Regular expression: first 18 characters alphanumeric (A-Z, 0-9) and last 2 must be digits.
  const regex = /^[A-Z0-9]{18}[0-9]{2}$/;
  if (!regex.test(lei)) {
    return false;
  }

  // Convert the first 18 characters: digits remain the same; letters are converted (A=10, ..., Z=35)
  let numericStr = '';
  for (let i = 0; i < 18; i++) {
    const char = lei[i];
    if (/[0-9]/.test(char)) {
      numericStr += char;
    } else if (/[A-Z]/.test(char)) {
      // Convert letter to number: 'A'.charCodeAt(0) = 65 so A -> 10, B -> 11, ... Z -> 35.
      numericStr += (char.charCodeAt(0) - 55).toString();
    } else {
      // Shouldn't happen because of regex check, but just in case.
      return false;
    }
  }

  // Append "00" for the check digit calculation
  numericStr += '00';

  // Use BigInt for large number arithmetic to compute modulus 97
  const remainder = BigInt(numericStr) % BigInt(97);
  const calculatedCheck = 98 - Number(remainder);

  // Format check digits as two digits (prepend '0' if needed)
  const checkDigits =
    calculatedCheck < 10
      ? '0' + calculatedCheck.toString()
      : calculatedCheck.toString();

  // Compare computed check digits with the last two digits of the LEI
  return lei.slice(18) === checkDigits;
};

export const kybValidators: Validators<KybFormData, KybFormData> = {
  contactEmail: (email: string | null) => {
    if (email === '' || email === null) return undefined;
    return !/(.+)@(.+)\.(.+)$/.test(email)
      ? 'Please enter a valid email.'
      : undefined;
  },
  settlementReportEmails: (emails: string | null) => {
    if (emails === '' || emails === null) return undefined;
    const emailArray = emails.split(',').map((email) => email.trim());
    const isValid = emailArray.every((email) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    );
    return isValid ? undefined : 'Please enter valid email(s).';
  },
  fullCompanyName: (name: string) =>
    name.length < 1 ? 'Can not be empty.' : undefined,
  registrationNumber: (number: string, currentValues: KybFormData) => {
    if (currentValues.incorporationCountry === 'SE') {
      return !/[\d]{6}-[\d]{4}/.test(number)
        ? 'Needs to be on the format XXXXXX-XXXX.'
        : undefined;
    }
    return number.length < 1 ? 'Can not be empty.' : undefined;
  },
  leiNumber: (lei: string | null) => {
    if (lei === '' || lei === null) return undefined;
    return isValidLEI(lei) ? undefined : 'Invalid LEI number.';
  },
  incorporationDate: (date: Date | null) => {
    if (date === null) return undefined;
    if (date > new Date()) return 'Cannot be in the future.';
    return undefined;
  },
  onboardingDate: (date: Date | null) => {
    if (date === null) return 'This needs to be set.';
    if (date > new Date()) return 'Cannot be in the future.';
    return undefined;
  },
  incorporationCountry: (country: string | undefined) =>
    !country ? 'This needs to be set.' : undefined,
  expectedMonthlyVolume: (volume: number) =>
    volume < 0 ? 'Can not have negative values.' : undefined,
  expectedMonthlyTransactions: (pay: number) =>
    pay < 0 ? 'Can not have negative values.' : undefined,
  turnover: (turnover: number) =>
    turnover < 0 ? 'Can not have negative values.' : undefined,
  jurisdictions: (jurisdictions: string) =>
    !jurisdictions || jurisdictions.trim().length < 1
      ? 'Can not be empty.'
      : undefined,
  registeredAddress: {
    address1: (address: string) =>
      !address || address.trim().length < 1 ? 'Can not be empty.' : undefined,
    country: (country: string) =>
      !country || country.trim().length < 1 ? 'Can not be empty.' : undefined,
    postalNumber: (postalNumber: string) =>
      !postalNumber || postalNumber.trim().length < 1
        ? 'Can not be empty.'
        : undefined,
    postalRegion: (postalRegion: string) =>
      !postalRegion || postalRegion.trim().length < 1
        ? 'Can not be empty.'
        : undefined,
  },
  primaryAddress: {
    address1: (address: string) =>
      !address || address.trim().length < 1 ? 'Can not be empty.' : undefined,
    country: (country: string) =>
      !country || country.trim().length < 1 ? 'Can not be empty.' : undefined,
    postalNumber: (postalNumber: string) =>
      !postalNumber || postalNumber.trim().length < 1
        ? 'Can not be empty.'
        : undefined,
    postalRegion: (postalRegion: string) =>
      !postalRegion || postalRegion.trim().length < 1
        ? 'Can not be empty.'
        : undefined,
  },
  relatedPeople: {
    firstName: (firstName: string) =>
      !firstName || firstName.trim().length < 1
        ? 'Can not be empty.'
        : undefined,
    lastName: (lastName: string) =>
      !lastName || lastName.trim().length < 1 ? 'Can not be empty.' : undefined,
    dateOfBirth: (date: Date | null) => {
      if (date === null) return 'Needs to be set.';
      if (date > new Date()) return 'Cannot be in the future.';
      return undefined;
    },
    ssn: (ssn: string) =>
      !ssn || ssn.trim().length < 1 ? 'Can not be empty.' : undefined,
    type: (type: string | undefined) =>
      !type ? 'This needs to be set.' : undefined,
    placeOfBirth: (placeOfBirth: string) =>
      placeOfBirth && placeOfBirth.length > 100
        ? 'Can not be longer than 100 characters.'
        : undefined,
  },
};
